import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { blogService } from '../../services/blogService';
import { blogFeedbackService } from '../../services/blogFeedbackService';
import ActionMenu from '../../components/common/ActionMenu';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import BlogFilters from '../../components/common/BlogFilters';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';
import Pagination from '../../components/common/Pagination';

const INITIAL_FILTERS = { search: '', status: '', categoryId: '', tagId: '' };

const Blogs = () => {
   const navigate = useNavigate();
   const [blogs, setBlogs] = useState([]);
   const [loading, setLoading] = useState(true);
   const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
   const [filters, setFilters] = useState(INITIAL_FILTERS);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);
   const [sortBy, setSortBy] = useState('createdAt');
   const [sortOrder, setSortOrder] = useState('desc');
   const [feedbackByBlogId, setFeedbackByBlogId] = useState({});

   const fetchBlogs = useCallback(async () => {
      try {
         setLoading(true);
         const params = {
            page: pagination.page,
            limit: pagination.limit,
            sortBy,
            sortOrder,
            list: true,
            ...(filters.status && { status: filters.status }),
            ...(filters.search && { search: filters.search }),
            ...(filters.categoryId && { categoryId: filters.categoryId }),
            ...(filters.tagId && { tagId: filters.tagId }),
         };
         const response = await blogService.getAll(params);
         setBlogs(response.data);
         setPagination(response.pagination);
      } catch (error) {
         console.error('Error fetching blogs:', error);
      } finally {
         setLoading(false);
      }
   }, [pagination.page, pagination.limit, filters, sortBy, sortOrder]);

   const handleSortByViews = () => {
      if (sortBy === 'viewCount') {
         setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      } else {
         setSortBy('viewCount');
         setSortOrder('desc');
      }
      setPagination((p) => ({ ...p, page: 1 }));
   };

   useEffect(() => {
      fetchBlogs();
   }, [fetchBlogs]);

   // Feedback totals only for the posts on the current page
   const blogIdsKey = blogs.map((b) => b.id).join(',');
   useEffect(() => {
      if (!blogIdsKey) return;
      const fetchFeedback = async () => {
         try {
            const res = await blogFeedbackService.getSummary(blogIdsKey);
            const byId = {};
            res.data.data.forEach(({ blog, helpfulCount, notHelpfulCount }) => {
               byId[blog.id] = { helpfulCount, notHelpfulCount };
            });
            setFeedbackByBlogId(byId);
         } catch (error) {
            console.error('Error fetching blog feedback:', error);
         }
      };
      fetchFeedback();
   }, [blogIdsKey]);

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await blogService.delete(deleteTarget.id);
         toast.success('Blog deleted successfully');
         setDeleteTarget(null);
         fetchBlogs();
      } catch (error) {
         console.error('Error deleting blog:', error);
         toast.error(error.response?.data?.message || 'Failed to delete blog');
      } finally {
         setDeleting(false);
      }
   };

   if (loading && blogs.length === 0) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Blogs</h1>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Manage your blog posts</p>
               </div>
               <Link to="/admin/blogs/new">
                  <Button>Create New Blog</Button>
               </Link>
            </div>

            <BlogFilters
               filters={filters}
               onChange={(newFilters) => {
                  setFilters(newFilters);
                  setPagination((p) => ({ ...p, page: 1 }));
               }}
               onReset={() => {
                  setFilters(INITIAL_FILTERS);
                  setPagination((p) => ({ ...p, page: 1 }));
               }}
            />

            <div
               className={`rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden transition-opacity ${
                  loading ? 'opacity-50 pointer-events-none' : ''
               }`}
               aria-busy={loading}
            >
               <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Title
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Category
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Status
                           </th>
                           <th
                              onClick={handleSortByViews}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                           >
                              <span className="inline-flex items-center gap-1">
                                 Views
                                 {sortBy === 'viewCount' && (
                                    <span>{sortOrder === 'desc' ? '↓' : '↑'}</span>
                                 )}
                              </span>
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Feedback
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Created
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                        {blogs.length === 0 ? (
                           <tr>
                              <td
                                 colSpan="7"
                                 className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                              >
                                 No blogs found
                              </td>
                           </tr>
                        ) : (
                           blogs.map((blog) => (
                              <tr
                                 key={blog.id}
                                 onClick={() => navigate(`/admin/blogs/${blog.id}`)}
                                 className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                              >
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                       {blog.title}
                                    </div>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                       {blog.categories.map((c) => c.name).join(', ')}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                       className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          blog.status === 'PUBLISHED'
                                             ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                             : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                       }`}
                                    >
                                       {blog.status}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                    {(blog.viewCount ?? 0).toLocaleString()}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {feedbackByBlogId[blog.id] ? (
                                       <span className="flex items-center gap-2">
                                          <span className="text-green-700 dark:text-green-400">
                                             👍 {feedbackByBlogId[blog.id].helpfulCount}
                                          </span>
                                          <span className="text-red-700 dark:text-red-400">
                                             👎 {feedbackByBlogId[blog.id].notHelpfulCount}
                                          </span>
                                       </span>
                                    ) : (
                                       <span className="text-gray-300 dark:text-gray-600">—</span>
                                    )}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <DateTimeDisplay dateStr={blog.createdAt} />
                                 </td>
                                 <td
                                    className="px-6 py-4 whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                 >
                                    <ActionMenu
                                       items={[
                                          {
                                             label: 'Edit',
                                             onClick: () => navigate(`/admin/blogs/${blog.id}`),
                                          },
                                          {
                                             label: 'Delete',
                                             variant: 'danger',
                                             onClick: () => setDeleteTarget(blog),
                                          },
                                       ]}
                                    />
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>
            </div>

            <Pagination
               page={pagination.page}
               totalPages={pagination.totalPages}
               total={pagination.total}
               limit={pagination.limit}
               onPageChange={(p) => setPagination({ ...pagination, page: p })}
            />
         </div>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Blog"
            message={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default Blogs;
